import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Leaf, MessageCircle, CheckCircle, Loader2, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const VerifyPhonePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const phone = searchParams.get('phone') || '';
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [verified, setVerified] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  const inputRefs = useRef([]);

  // Countdown for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Handle code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only digits
    
    const newCode = [...code];
    newCode[index] = value.slice(-1); // Only last digit
    setCode(newCode);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (newCode.every(d => d !== '') && newCode.join('').length === 6) {
      handleVerify(newCode.join(''));
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newCode = pastedData.split('');
      setCode(newCode);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (verificationCode) => {
    if (!phone) {
      toast.error('Numéro de téléphone manquant');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/api/auth/verify-phone`, {
        phone: phone,
        code: verificationCode
      });
      
      if (response.data.verified) {
        setVerified(true);
        toast.success('Compte vérifié avec succès !');
        setTimeout(() => navigate('/auth'), 2000);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Code incorrect';
      toast.error(message);
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!phone || countdown > 0) return;
    
    setResending(true);
    try {
      const response = await axios.post(`${API}/api/auth/resend-code?phone=${encodeURIComponent(phone)}`);
      toast.success('Nouveau code envoyé !');
      setCountdown(60); // 60 seconds cooldown
      
      // Open WhatsApp link if provided
      if (response.data.whatsapp_link) {
        window.open(response.data.whatsapp_link, '_blank');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi du code');
    } finally {
      setResending(false);
    }
  };

  if (verified) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center">
          <div className="glass-card rounded-2xl p-8">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-emerald-500" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Compte vérifié !</h1>
            <p className="text-zinc-400 mb-6">Redirection vers la connexion...</p>
            <Loader2 className="w-6 h-6 text-emerald-500 mx-auto animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Back Link */}
        <Link to="/auth" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6">
          <ArrowLeft className="w-4 h-4" />
          Retour à la connexion
        </Link>

        <div className="glass-card rounded-2xl p-8">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="p-2 bg-emerald-500/20 rounded-xl">
              <Leaf className="w-6 h-6 text-emerald-500" />
            </div>
            <span className="text-emerald-500 font-bold text-xl">YOUMI GREEN</span>
            <span className="text-white font-bold text-xl">VTC</span>
          </div>

          {/* WhatsApp Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-[#25D366]/20 rounded-full flex items-center justify-center">
              <MessageCircle className="w-8 h-8 text-[#25D366]" />
            </div>
          </div>

          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">
              Vérification WhatsApp
            </h1>
            <p className="text-zinc-400 text-sm">
              Entrez le code à 6 chiffres envoyé au
            </p>
            <p className="text-emerald-400 font-medium mt-1">{phone}</p>
          </div>

          {/* Code Input */}
          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {code.map((digit, index) => (
              <Input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleCodeChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="w-12 h-14 text-center text-2xl font-bold bg-zinc-800 border-zinc-700 text-white focus:border-emerald-500"
                disabled={loading}
                data-testid={`code-input-${index}`}
              />
            ))}
          </div>

          {/* Verify Button */}
          <Button
            onClick={() => handleVerify(code.join(''))}
            disabled={code.join('').length !== 6 || loading}
            className="w-full btn-primary mb-4"
            data-testid="verify-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <CheckCircle className="w-4 h-4 mr-2" />
            )}
            Vérifier
          </Button>

          {/* Resend Code */}
          <div className="text-center">
            <p className="text-zinc-500 text-sm mb-2">
              Vous n'avez pas reçu le code ?
            </p>
            <Button
              variant="ghost"
              onClick={handleResendCode}
              disabled={resending || countdown > 0}
              className="text-emerald-400 hover:text-emerald-300"
              data-testid="resend-code-btn"
            >
              {resending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              {countdown > 0 ? `Renvoyer dans ${countdown}s` : 'Renvoyer le code'}
            </Button>
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg">
            <p className="text-zinc-400 text-xs text-center">
              Le code est envoyé via WhatsApp. Vérifiez vos messages.
              Si vous ne l'avez pas reçu, cliquez sur "Renvoyer le code".
            </p>
          </div>
        </div>

        {/* Footer */}
        <p className="text-zinc-500 text-sm text-center mt-6">
          YOUMI GREEN VTC - Service VTC 100% électrique à Nice
        </p>
      </div>
    </div>
  );
};

export default VerifyPhonePage;
